import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Grid,
  Box,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  Select,
  FormHelperText,
} from '@mui/material';
import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';

import { addInterfacesRequest, updateInterface } from '../../../network/apis';
import { ReactComponent as DropdownIcon } from '../../../theme/images/deopdownIcon.svg';

export default function InterfaceForm({ handleClose, mode = 'add', data = {} }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const categoryOptions = [
    { id: 1, labelAr: 'سكني', labelEn: 'Residential', value: 'RESIDENTIAL' },
    { id: 2, labelAr: 'تجاري', labelEn: 'Commercial', value: 'COMMERCIAL' },
  ];

  const validationSchema = yup.object({
    nameEn: yup
      .string()
      .required(t('Validation.requiredField'))
      .test('is-english', t('Validation.englishReq'), (value) => {
        const englishRegex = /^[A-Za-z0-9_\s]*$/;
        return englishRegex.test(value);
      }),

    nameAr: yup
      .string()
      .required(t('Validation.requiredField'))
      .test('is-arabic', t('Validation.arabicReq'), (value) => {
        const arabicRegex = /[\u0600-\u06FF]/;
        const englishRegex = /[A-Za-z0-9]/;
        return arabicRegex.test(value) && !englishRegex.test(value);
      }),

    category: yup
      .string()
      .required(t('Validation.requiredField')),
  });

  return (
    <Formik
      initialValues={{
        nameEn: data?.nameEn || '',
        nameAr: data?.nameAr || '',
        category: data?.category || '',
      }}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setLoading(true);

        const apiCall =
          mode === 'edit'
            ? updateInterface({
                id: data?.id,
                nameEn: values.nameEn,
                nameAr: values.nameAr,
                category: values.category,
              })
            : addInterfacesRequest(values);

        apiCall
          .then((response) => {
            setLoading(false);
            if (response?.data?.STATUS === 'FAILED' || response?.data?.STATUS === '0') {
              toast.error(response?.data?.MESSAGE || response?.data?.message);
            } else {
              toast.success(response?.data?.MESSAGE || response?.data?.message);
              handleClose();
            }
          })
          .catch((err) => {
            setLoading(false);
            toast.error(err?.response?.data?.MESSAGE || err?.response?.data?.message || err?.message);
          });
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Grid rowSpacing={2} container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('General.nameEn')}
              </Typography>
              <TextField
                name="nameEn"
                required
                fullWidth
                value={values.nameEn}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nameEn && Boolean(errors.nameEn)}
                helperText={touched.nameEn && errors.nameEn}
              />
            </Grid>
            <Grid item xs={2} sm={4} md={6}>
              <Typography variant="body1" gutterBottom>
                {t('General.nameAr')}
              </Typography>
              <TextField
                name="nameAr"
                required
                fullWidth
                value={values.nameAr}
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nameAr && Boolean(errors.nameAr)}
                helperText={
                  touched.nameAr &&
                  errors.nameAr && <span dangerouslySetInnerHTML={{ __html: errors.nameAr.replace('\n', '<br />') }} />
                }
              />
            </Grid>

            <Grid item xs={4} sm={8} md={12}>
              <Typography variant="body1" gutterBottom>
                {t('interface.category')}
              </Typography>
              <FormControl fullWidth error={touched.category && Boolean(errors.category)}>
                <Select
                  name="category"
                  value={values.category}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  displayEmpty
                  sx={{ paddingX: '10px', borderRadius: '10px' }}
                  renderValue={(selected) => {
                    if (!selected)
                      return (
                        <Typography color="text.secondary">
                          {t('interface.selectCategory')}
                        </Typography>
                      );
                    const item = categoryOptions.find((o) => o.value === selected);
                    return (
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography margin="0">
                          {isRtl ? item?.labelAr : item?.labelEn}
                        </Typography>
                      </Box>
                    );
                  }}
                  IconComponent={() => (
                    <DropdownIcon style={{ width: '15px', height: '15px' }} />
                  )}
                >
                  <MenuItem disabled value="">
                    <Typography color="text.secondary">
                      {t('interface.selectCategory')}
                    </Typography>
                  </MenuItem>
                  {categoryOptions.map((item, index) => (
                    <MenuItem key={index} value={item?.value}>
                      <Typography margin="0">
                        {isRtl ? item?.labelAr : item?.labelEn}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {touched.category && errors.category && (
                  <FormHelperText error>{errors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingX: '20px',
              }}
            >
              {' '}
              <Button
                sx={{ mt: 4, border: '1px solid #111827' }}
                size="large"
                onClick={handleClose}
                type="button"
                variant=""
              >
                {t('General.cancel')}
              </Button>
              <LoadingButton sx={{ mt: 4 }} loading={loading} size="large" type="submit" variant="contained">
                {t('General.save')}
              </LoadingButton>
            </Box>
          </Grid>
        </form>
      )}
    </Formik>
  );
}
